import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { CircularProgress } from '@mui/material';

const SchemeWiseCommoditiesSubsidiesReport = () => {
    const [districts, setDistricts] = useState([]);
    const [offices, setOffices] = useState([]);
    const [fpsList, setFpsList] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [lastModified, setLastModified] = useState(null);
    const [filters, setFilters] = useState({
        distCode: '0',
        afsoCode: '0',
        fpsId: '0'
    });
    const [currentMonthYear, setCurrentMonthYear] = useState({ month: '', year: '' });
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchDistricts();
        fetchAllOffices();
        fetchCurrentMonthYear();
    }, []);

    const clearError = () => {
        setErrorMessage('');
    };

    const fetchCurrentMonthYear = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/current-month-year`);
            const { month, year } = response.data;
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            setCurrentMonthYear({ month: monthNames[month - 1], year });
        } catch (error) {
            console.error('Error fetching month/year:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchDistricts = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/districts`);
            setDistricts(response.data);
        } catch (error) {
            console.error('Error fetching districts:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchAllOffices = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/offices?distCode=0`);
            setOffices(response.data);
        } catch (error) {
            console.error('Error fetching all offices:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchFpsByOffice = async (afsoCode) => {
        try {
            clearError();
            if (afsoCode === '0') {
                setFpsList([]);
            } else {
                const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/fps?afsoCode=${afsoCode}`);
                const formattedFps = response.data.map(fps => ({
                    fpsId: fps.fpsId,
                    ownername: fps.ownername || ''
                }));
                setFpsList(formattedFps);
            }
        } catch (error) {
            console.error('Error fetching FPS:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const handleDistrictChange = async (e) => {
        const distCode = e.target.value;
        setFilters(prev => ({ ...prev, distCode, afsoCode: '0', fpsId: '0' }));
        setFpsList([]);
        setReportData([]);
        setHasSearched(false);

        if (distCode === '0') {
            await fetchAllOffices();
        } else {
            try {
                clearError();
                const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/offices?distCode=${distCode}`);
                setOffices(response.data);
            } catch (error) {
                console.error('Error fetching offices:', error);
                if (error.response) {
                    setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
                } else if (error.request) {
                    setErrorMessage('Invalid Request (Exception).');
                } else {
                    setErrorMessage('Invalid Request (Exception)');
                }
            }
        }
    };

    const handleOfficeChange = async (e) => {
        const afsoCode = e.target.value;
        setFilters(prev => ({ ...prev, afsoCode, fpsId: '0' }));
        setReportData([]);
        setHasSearched(false);
        await fetchFpsByOffice(afsoCode);
    };

    const handleFpsChange = (e) => {
        setFilters(prev => ({ ...prev, fpsId: e.target.value }));
        setReportData([]);
        setHasSearched(false);
    };

    const areAllFiltersAll = filters.distCode === '0' && filters.afsoCode === '0' && filters.fpsId === '0';

    const getTotalColumns = () => {
        let total = 7; 
        
        if (!areAllFiltersAll) {
            if (filters.distCode === '0') total += 1; 
            if (filters.afsoCode === '0') total += 1;  
            if (filters.fpsId === '0') total += 1;
        }
        
        return total;
    };

    const handleGenerateReport = async () => {
        setLoading(true);
        setHasSearched(true);
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy-report/report`, { params: filters });
            setReportData(response.data);
            setLastModified(dayjs().format('DD-MM-YYYY HH:mm:ss'));
        } catch (error) {
            console.error('Error generating report:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDisplayValue = (value) =>
        value === null || value === undefined || isNaN(value)
            ? '0.00'
            : parseFloat(value).toFixed(2);

    const totalColumns = getTotalColumns();

    return (
        <div className="wrapper row3" style={{ paddingBottom: '40px', overflow: 'hidden', position: 'relative' }}>
            <div id="container">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <table width="80%">
                        <thead>
                            <tr className="tableheader">
                                <th colSpan={6}>Subsidy Rates For Commodities</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><b>District</b></td>
                                <td>
                                    <select value={filters.distCode} onChange={handleDistrictChange} style={{ width: '80%', padding: '6px' }}>
                                        <option value="0">--ALL--</option>
                                        {districts.map(d => (
                                            <option key={d.distCode} value={d.distCode}>{d.distNameEn}</option>
                                        ))}
                                    </select>
                                </td>
                                <td><b>Office</b></td>
                                <td>
                                    <select value={filters.afsoCode} onChange={handleOfficeChange} style={{ width: '80%', padding: '6px' }}>
                                        <option value="0">--ALL--</option>
                                        {offices.map(o => (
                                            <option key={o.officeCode} value={o.officeCode}>{o.officeNameEn}</option>
                                        ))}
                                    </select>
                                </td>
                                <td><b>FPS Id</b></td>
                                <td>
                                    <select value={filters.fpsId} onChange={handleFpsChange} style={{ width: '80%', padding: '6px' }}>
                                        <option value="0">--ALL--</option>
                                        {fpsList.map(fps => (
                                            <option key={fps.fpsId} value={fps.fpsId}>
                                                {fps.fpsId} {fps.ownername ? ` - ${fps.ownername}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '8px' }}>
                                    <button
                                        className="button button2"
                                        onClick={handleGenerateReport}
                                        disabled={loading}>
                                        SUBMIT
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Error Message Display */}
                {errorMessage && (
                    <div style={{ textAlign: 'center', color: 'red', marginTop: '20px' }}>
                        {errorMessage}
                    </div>
                )}

                {loading && (
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <CircularProgress />
                    </div>
                )}

                {!loading && reportData.length > 0 && (
                    <div style={{ marginTop: "20px", textAlign: "center" }}>
                        <table border="1" style={{ margin: "0 auto", width: areAllFiltersAll ? "80%" : "95%", borderCollapse: 'collapse' }}>
                            <thead>
                                <tr className="tableheader">
                                    <th colSpan={totalColumns}>
                                        Subsidy Rates For Commodities – {currentMonthYear.month}, {currentMonthYear.year}
                                    </th>
                                </tr>
                                <tr className="tableheader">
                                    <th>Sl No.</th>
                                    {/* Only show these columns when NOT all filters are "ALL" */}
                                    {!areAllFiltersAll && (
                                        <>
                                            {filters.distCode === '0' && <th>District</th>}
                                            {filters.afsoCode === '0' && <th>Office</th>}
                                            {filters.fpsId === '0' && <th>FPS ID</th>}
                                        </>
                                    )}
                                    <th>Commodity Name</th>
                                    <th>Scheme Name</th>
                                    <th>Economic Cost</th>
                                    <th>Central Subsidy Price</th>
                                    <th>State Subsidy Price</th>
                                    <th>Commodity Sales Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                        {/* Only show these columns when NOT all filters are "ALL" */}
                                        {!areAllFiltersAll && (
                                            <>
                                                {filters.distCode === '0' && <td style={{ textAlign: 'center' }}>{item.distNameEn}</td>}
                                                {filters.afsoCode === '0' && <td style={{ textAlign: 'center' }}>{item.officeNameEn}</td>}
                                                {filters.fpsId === '0' && <td style={{ textAlign: 'center' }}>{item.fpsId}</td>}
                                            </>
                                        )}
                                        <td style={{ textAlign: 'center' }}><strong>{item.commNameEn}</strong></td>
                                        <td style={{ textAlign: 'center' }}><strong>{item.schemeNameEn}</strong></td>
                                        <td style={{ textAlign: 'right', paddingRight: '10px' }}>{formatDisplayValue(item.economicPrice)}</td>
                                        <td style={{ textAlign: 'right', paddingRight: '10px' }}>{formatDisplayValue(item.subsidyPrice)}</td>
                                        <td style={{ textAlign: 'right', paddingRight: '10px' }}>{formatDisplayValue(item.stateSubsidyPrice)}</td>
                                        <td style={{ textAlign: 'right', paddingRight: '10px' }}>{formatDisplayValue(item.commSelPrice)}</td>
                                    </tr>
                                ))}
                                <tr style={{ backgroundColor: 'black', color: 'white' }}>
                                    <td colSpan={totalColumns} style={{ textAlign: 'right', paddingRight: '10px' }}>
                                        Last Modified: {lastModified}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {hasSearched && !loading && reportData.length === 0 && !errorMessage && (
                    <div style={{ textAlign: 'center', color: 'red', marginTop: '20px' }}>
                        No records found for the selected criteria.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchemeWiseCommoditiesSubsidiesReport;